import React from 'react';
import { useCookies } from 'react-cookie';
import { useListLiveProjectsQuery } from '../../generated/core-api/graphql-react-query';
import useAudioConfig, { AudioConfig } from './Hooks/useAudioConfig';
import { SessionState } from './Hooks/useSession';
import useDecisionTree, { DecisionTreeState } from './Hooks/useDecisionTrees';
import useCardPreload from './Hooks/useCardPreload';
import useNewSession from './Hooks/useNewSession';

interface LiveContextProps {
  projectId: string | null;
  setProjectId: (projectId: string | null) => void;
  displayId: string | null;
  setDisplayId: (displayId: string | null) => void;
  search: string | null;
  setSearch: (search: string | null) => void;
  audioConfig: AudioConfig;
  sessionState: SessionState;
  decisionTreeState: DecisionTreeState;
}

export const LiveContext = React.createContext<LiveContextProps>({
  projectId: null,
  setProjectId: () => { },
  displayId: null,
  setDisplayId: () => { },
  search: null,
  setSearch: () => { },
  audioConfig: {} as any,
  sessionState: {} as any,
  decisionTreeState: {} as any,
});

export function LiveContextProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [cookies, setCookies, clearCookies] = useCookies(['projectId']);
  const [projectId, setProjectId] = React.useState<string | null>(cookies.projectId ?? null);
  const [displayId, setDisplayId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState<string | null>(null);
  const audioConfig = useAudioConfig();
  const decisionTreeState = useDecisionTree();
  const sessionState = useNewSession(projectId, audioConfig, setProjectId);

  function updateProjectId(newValue: string | null) {
    if (newValue === null) clearCookies('projectId', { path: '/' });
    else {
      setCookies(
        'projectId',
        newValue,
        {
          path: '/', maxAge: 60 * 60 * 24 * 365, secure: true, sameSite: 'strict',
        },
      );
    }
    setProjectId(newValue);
  }

  const projects = useListLiveProjectsQuery({});
  React.useEffect(() => {
    if (projects.data) {
      if (projects.data.listProjects.length === 0) {
        updateProjectId(null);
      } else if (!projectId || !projects.data.listProjects.map((p) => p.id).includes(projectId)) {
        updateProjectId(projects.data.listProjects[0].id);
      }
    }
  }, [projects.data, projectId]);

  useCardPreload(projectId);

  const value = React.useMemo(() => ({
    projectId,
    setProjectId: updateProjectId,
    displayId,
    setDisplayId,
    search,
    setSearch,
    audioConfig,
    decisionTreeState,
    sessionState,
  }), [projectId, search, audioConfig, decisionTreeState, sessionState]);
  return (
    <LiveContext.Provider value={value}>
      {children}
    </LiveContext.Provider>
  );
}

export default function useLiveContext() {
  return React.useContext(LiveContext);
}
